import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import {
  postCommentReplyPath,
  postCommentReplyUsefulPath,
} from "../../../../routes";
import { HelperImageFile } from "../../../shared/lib/types";
import LinkedText from "../../shared/components/ui/LinkedText";
import { Box } from "../../shared/components/ui/chakra";
import FilesWithoutImage from "../../shared/components/app/FilesWithoutImage";
import ImagesWithSlider from "../../shared/components/app/ImagesWithSlider";
import MoreMenu from "../../shared/components/app/MoreMenu";
import PostResourceAvatar from "../../shared/components/app/PostResourceAvatar";
import PostResourceDisplayName from "../../shared/components/app/PostResourceDisplayName";
import { SharedApprovedCurrentUser } from "../../shared/lib/types";
import useFlash from "../../shared/lib/useFlash";
import useRequest from "../../shared/lib/useRequest";
import { PostsShowProps, UserPostCommentReply } from "../lib/types";
import { EditModal, ReplyFormFields, useReplyForm } from "./CommentReplyForm";
import NewUsefulButton from "./NewUsefulButton";
import UserProfileLink from "./UserProfileLink";

import { Flex, HStack, useDisclosure } from "@chakra-ui/react";

const ReplyWrapper = ({
  reply: propReply,
  currentUser,
  post,
}: {
  reply: UserPostCommentReply;
  currentUser: SharedApprovedCurrentUser;
  post: PostsShowProps;
}) => {
  const [reply, setReply] = useState(propReply);
  return (
    <Reply
      key={reply.updated_at}
      reply={reply}
      currentUser={currentUser}
      onUpdated={setReply}
      post={post}
    />
  );
};

const EditReplyModal = ({
  reply,
  onUpdated,
  editModalDisclosure,
  currentUser,
}: {
  reply: UserPostCommentReply;
  onUpdated: (data: UserPostCommentReply) => void;
  editModalDisclosure: ReturnType<typeof useDisclosure>;
  currentUser: SharedApprovedCurrentUser;
}) => {
  const request = useRequest();
  const showFlash = useFlash();

  const editReplyFormState = useReplyForm({
    onSubmit: async (data) => {
      const res = await request(postCommentReplyPath(reply.code), "PUT", data);
      if (res.ok) {
        onUpdated(await res.json() as UserPostCommentReply);
      } else if (res.status === 404) {
        showFlash({
          error: (
            <>
              返信が見つかりませんでした。
              <br />
              ページを再読み込みしてください。
            </>
          ),
        });
      }
    },
    defaultValues: { content: reply.content },
    defaultImageFiles: reply.image_files,
    defaultFilesWithoutImage: reply.files_without_image,
  });

  return (
    <EditModal
      isOpen={editModalDisclosure.isOpen}
      onClose={editModalDisclosure.onClose}
      formState={editReplyFormState}
      title="返信編集"
    >
      <ReplyFormFields
        formState={editReplyFormState}
        currentUser={currentUser}
        anonymous={reply.author.anonymous}
      />
    </EditModal>
  );
};

const Reply = ({
  reply,
  currentUser,
  onUpdated,
  post,
}: {
  reply: UserPostCommentReply;
  currentUser: SharedApprovedCurrentUser;
  onUpdated: (reply: UserPostCommentReply) => void;
  post: PostsShowProps;
}) => {
  const editModalDisclosure = useDisclosure();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current == null) return;

    const search = new URLSearchParams(location.search);
    if (reply.code == search.get("reply_code")) {
      ref.current.scrollIntoView({ block: "start" });
      // ピッタリはみづらいので微調整
      window.scrollTo(0, window.scrollY - 24);
    }
    // 初回のみ実行したいので
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box ref={ref}>
        <Flex justifyContent="space-between">
          <HStack gap={3}>
            <UserProfileLink author={reply.author} currentUser={currentUser}>
              <PostResourceAvatar author={reply.author} />
            </UserProfileLink>
            <Box>
              <UserProfileLink author={reply.author} currentUser={currentUser}>
                <PostResourceDisplayName author={reply.author} />
              </UserProfileLink>
              <HStack columnGap={4} rowGap={0} wrap="wrap">
                <Box fontSize="xs" color="#848484">
                  投稿日：{dayjs(reply.created_at).format("L LT")}
                </Box>
                {reply.updated_at !== "" &&
                  reply.created_at !== reply.updated_at && (
                    <Box fontSize="xs" color="#848484">
                      最終更新日：{dayjs(reply.updated_at).format("L LT")}
                    </Box>
                  )}
              </HStack>
            </Box>
          </HStack>
          {!post.is_resolved && reply.is_own && (
            <MoreMenu
              buttonProps={{
                "data-testid": "post-comment-reply-detail-button",
              }}
              listItemProps={{
                as: "button",
                textAlign: "left",
                onClick: editModalDisclosure.onOpen,
              }}
              listItemText="返信を編集"
            />
          )}
        </Flex>
        <Box wordBreak="break-word" whiteSpace="pre-wrap" w="full" mt={4}>
          <LinkedText>{reply.content}</LinkedText>
        </Box>
        {reply.files_without_image.length !== 0 && (
          <Box mt={5}>
            <FilesWithoutImage files={reply.files_without_image} />
          </Box>
        )}
        {reply.image_files.length !== 0 && (
          <Box mt={5}>
            <ImagesWithSlider
              urls={reply.image_files.map((file: HelperImageFile) => file.url)}
            />
          </Box>
        )}
        <Flex justifyContent="right" mt={4}>
          <NewUsefulButton
            useful={reply.is_marked_useful}
            count={reply.useful_count}
            path={postCommentReplyUsefulPath(reply.code)}
            isDisabled={reply.is_own}
          />
        </Flex>
      </Box>
      <EditReplyModal
        reply={reply}
        onUpdated={onUpdated}
        editModalDisclosure={editModalDisclosure}
        currentUser={currentUser}
      />
    </Box>
  );
};

export default ReplyWrapper;
